import React from 'react';
import { useMutation } from '@apollo/client';
import { ExternalLink } from 'lucide-react';

import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalFooter
} from '@epoch-ui/components/Modal/modal';
import { Button } from '@epoch-ui/components/Button/button';
import { GENERATE_ZOOM_SETUP_LINK } from 'graphql/mutations/integrations/zoom';

interface ZoomConnectionModalProps extends React.ComponentPropsWithoutRef<typeof Modal> {
  /**
   * Redirect URL after successful Zoom connection
   * @default 'dashboard'
   */
  redirectTo?: string;
  /**
   * Whether this is a reconnection flow (user previously connected)
   * @default false
   */
  isReconnect?: boolean;
}

const ZoomConnectionModal: React.FC<ZoomConnectionModalProps> = ({
  redirectTo = 'dashboard',
  isReconnect = false,
  ...modalProps
}) => {
  const [generateZoomSetupLinkMutation, { loading }] = useMutation(GENERATE_ZOOM_SETUP_LINK, {
    onCompleted: (data) => {
      const zoomOauthLink = data?.generateZoomSetupLink?.link;
      if (zoomOauthLink) {
        window.location.href = zoomOauthLink;
      }
    },
    onError: (error) => {
      console.error('Failed to generate Zoom setup link:', error);
    }
  });

  const handleConnect = (): void => {
    generateZoomSetupLinkMutation({
      variables: { returnTo: redirectTo }
    });
  };

  const handleClose = (): void => {
    modalProps.onOpenChange?.(false);
  };

  return (
    <Modal {...modalProps}>
      <ModalContent size="sm" className="tw-max-w-[483px]">
        <ModalHeader>
          <ModalTitle>{isReconnect ? 'Sign in to Zoom' : 'Connect Zoom'}</ModalTitle>
        </ModalHeader>

        <div className="tw-text-sm tw-leading-5">
          {isReconnect ? (
            <p>To continue, please sign in to your Zoom account.</p>
          ) : (
            <p>
              To generate a Zoom link, connect your work account for Zoom.
              <br />
              <br />
              You&apos;ll also get access to additional reporting, such as guest attendance and
              external guests.
            </p>
          )}
        </div>

        <ModalFooter className="tw-flex-col-reverse tw-gap-3 xsm:tw-flex-row">
          <Button
            variant="secondary"
            size="sm"
            className="tw-w-full xsm:tw-w-auto"
            onClick={handleClose}
            disabled={loading}
          >
            Maybe later
          </Button>
          <Button
            size="sm"
            className="tw-w-full xsm:tw-w-auto"
            onClick={handleConnect}
            loading={loading}
            disabled={loading}
          >
            <ExternalLink className="tw-mr-2 tw-h-4 tw-w-4" />
            {isReconnect ? 'Sign in' : 'Connect'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export { ZoomConnectionModal };
